import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { interval, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SoftphoneEngineService } from '../../services/softphone-engine.service';
import { CallHistoryItem, DirectoryEntry, SoftphoneDirectory, TelephonieService } from '../../services/telephonie.service';
import { RealtimeService, RealtimeVoipCallEvent } from '../../services/realtime.service';

/**
 * VdSoftphoneWidgetComponent — softphone flottant global (overlay PWA).
 *
 * Monte dans app.component (comme la bulle IA) : visible partout, non bloquant.
 * S'active seulement si le module telephonie fournit une config (sinon reste
 * masque). On-brand (tokens vd-*), mobile-first, cibles tactiles >= 44px,
 * safe-area iOS, 100dvh sur le panneau plein ecran mobile.
 */
@Component({
  selector: 'vd-softphone-widget',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- ═══ APPEL ENTRANT : overlay plein ecran (prioritaire) ═══ -->
    <!-- Pilote par l'event SSE (instantane) OU par l'INVITE SIP, selon le 1er arrive. -->
    <div class="vd-sp-incoming" *ngIf="ringing()" role="dialog" aria-label="Appel entrant">
      <div class="vd-sp-inc-card">
        <div class="vd-sp-inc-pulse"><span class="material-icons-outlined">ring_volume</span></div>
        <div class="vd-sp-inc-label">Appel entrant</div>
        <div class="vd-sp-inc-name">{{ engine.remoteIdentity() || incomingHint()?.caller || 'Appel' }}</div>
        <div class="vd-sp-inc-actions">
          <button class="vd-sp-inc-reject" (click)="reject()" aria-label="Refuser">
            <span class="material-icons-outlined">call_end</span>
            <small>Refuser</small>
          </button>
          <button class="vd-sp-inc-accept" (click)="accept()" aria-label="Repondre">
            <span class="material-icons-outlined">call</span>
            <small>Repondre</small>
          </button>
        </div>
        <p class="vd-sp-inc-err" *ngIf="engine.errorMsg()">{{ engine.errorMsg() }}</p>
      </div>
    </div>

    <ng-container *ngIf="visible()">
    <!-- Bouton flottant (FAB) -->
    <button
      *ngIf="!open() && engine.status() !== 'incoming'"
      class="vd-sp-fab"
      [class.busy]="engine.isBusy()"
      (click)="toggle()"
      [attr.aria-label]="'Ouvrir le telephone'">
      <span class="material-icons-outlined">{{ fabIcon() }}</span>
    </button>

    <!-- Panneau -->
    <div class="vd-sp-panel" *ngIf="open()" role="dialog" aria-label="Telephone">
      <header class="vd-sp-head">
        <div class="vd-sp-id">
          <span class="material-icons-outlined dot" [class]="engine.status()">fiber_manual_record</span>
          <div>
            <div class="vd-sp-title">Telephone</div>
            <div class="vd-sp-sub">{{ statusLabel() }}<span *ngIf="engine.myNumber()"> · poste {{ engine.myNumber() }}</span></div>
          </div>
        </div>
        <button class="vd-sp-icon" (click)="toggle()" aria-label="Reduire">
          <span class="material-icons-outlined">expand_more</span>
        </button>
      </header>

      <!-- Etat: appel en cours / entrant -->
      <section class="vd-sp-call" *ngIf="engine.isBusy(); else idleTpl">
        <div class="vd-sp-peer">
          <div class="vd-sp-avatar"><span class="material-icons-outlined">person</span></div>
          <div class="vd-sp-peer-name">{{ engine.remoteIdentity() || 'Appel' }}</div>
          <div class="vd-sp-peer-state">
            <span *ngIf="engine.status() === 'incoming'">Appel entrant…</span>
            <span *ngIf="engine.status() === 'outgoing'">Appel…</span>
            <span *ngIf="engine.status() === 'in_call'">{{ elapsed() }}</span>
          </div>
        </div>

        <!-- Actions en appel (3x2) : muet / attente / DTMF / transfert / VM / rec auto -->
        <div class="vd-sp-actions" *ngIf="engine.status() === 'in_call'">
          <button class="vd-sp-act" [class.on]="engine.muted()" (click)="engine.toggleMute()">
            <span class="material-icons-outlined">{{ engine.muted() ? 'mic_off' : 'mic' }}</span>
            <small>Muet</small>
          </button>
          <button class="vd-sp-act" [class.on]="engine.onHold()" (click)="engine.toggleHold()">
            <span class="material-icons-outlined">pause</span>
            <small>Attente</small>
          </button>
          <button class="vd-sp-act" [class.on]="showKeypad()" (click)="showKeypad.set(!showKeypad())">
            <span class="material-icons-outlined">dialpad</span>
            <small>Clavier</small>
          </button>
          <button class="vd-sp-act" [class.on]="showTransfer()" (click)="toggleTransfer()">
            <span class="material-icons-outlined">phone_forwarded</span>
            <small>Transfert</small>
          </button>
          <button class="vd-sp-act" (click)="sendToVoicemail()" [disabled]="!engine.myNumber()">
            <span class="material-icons-outlined">voicemail</span>
            <small>Messagerie</small>
          </button>
          <button class="vd-sp-act" [class.on]="engine.speakerOn()" (click)="engine.toggleSpeaker()" type="button" title="Haut-parleur">
            <span class="material-icons-outlined">{{ engine.speakerOn() ? 'volume_up' : 'volume_off' }}</span>
            <small>HP</small>
          </button>
        </div>

        <!-- Transfert (REFER) : poste, file, IVR ou numero (guard si PSTN) -->
        <form class="vd-sp-transfer" *ngIf="engine.status() === 'in_call' && showTransfer()" (submit)="doTransfer($event)">
          <input
            class="vd-sp-num"
            [value]="transferTarget()"
            (input)="transferTarget.set($any($event.target).value)"
            placeholder="Poste, *80… ou numero"
            inputmode="tel"
            aria-label="Destination du transfert" />
          <button type="submit" class="vd-sp-transfer-go" [disabled]="!transferTarget()">
            <span class="material-icons-outlined">phone_forwarded</span>
          </button>
        </form>
        <div class="vd-sp-xfer-dir" *ngIf="engine.status() === 'in_call' && showTransfer()">
          <button type="button" class="vd-sp-dir-chip" *ngFor="let e of transferChoices()" (click)="pickTransfer(e)">
            {{ e.label }}
            <small>{{ e.dial }}</small>
          </button>
        </div>

        <!-- Clavier DTMF pendant l'appel -->
        <div class="vd-sp-pad" *ngIf="engine.status() === 'in_call' && showKeypad()">
          <button *ngFor="let k of keys" class="vd-sp-key" (click)="engine.sendDtmf(k)">{{ k }}</button>
        </div>

        <div class="vd-sp-callbtns">
          <button
            *ngIf="engine.status() === 'incoming'"
            type="button"
            class="vd-sp-answer"
            (click)="engine.answer()">
            <span class="material-icons-outlined">call</span>
          </button>
          <!-- Anti ghost-click : apres composition / decroche le bouton
               raccrocher est au meme endroit que Appeler → un touch residual
               coupait l'appel juste apres le 200 OK. -->
          <button
            type="button"
            class="vd-sp-hangup"
            [disabled]="hangupLocked()"
            (click)="safeHangup()">
            <span class="material-icons-outlined">call_end</span>
          </button>
        </div>
      </section>

      <!-- Etat repos: dialpad OU historique -->
      <ng-template #idleTpl>
        <nav class="vd-sp-tabs">
          <button [class.on]="idleView() === 'dial'" (click)="idleView.set('dial')">
            <span class="material-icons-outlined">dialpad</span> Clavier
          </button>
          <button [class.on]="idleView() === 'contacts'" (click)="openContacts()">
            <span class="material-icons-outlined">contacts</span> Contacts
          </button>
          <button [class.on]="idleView() === 'history'" (click)="openHistory()">
            <span class="material-icons-outlined">history</span> Historique
          </button>
        </nav>

        <section class="vd-sp-dial" *ngIf="idleView() === 'dial'">
          <input
            class="vd-sp-num"
            [value]="dialNumber()"
            (input)="dialNumber.set($any($event.target).value)"
            placeholder="Numero ou poste"
            inputmode="tel"
            aria-label="Numero a composer" />
          <div class="vd-sp-pad">
            <button *ngFor="let k of keys" class="vd-sp-key" (click)="press(k)">{{ k }}</button>
          </div>
          <div class="vd-sp-dialbtns">
            <button class="vd-sp-back" (click)="backspace()" [disabled]="!dialNumber()">
              <span class="material-icons-outlined">backspace</span>
            </button>
            <button class="vd-sp-call-btn" (click)="startCall()" [disabled]="!dialNumber() || engine.status() === 'connecting'">
              <span class="material-icons-outlined">call</span>
            </button>
          </div>
          <p class="vd-sp-err" *ngIf="engine.errorMsg()">{{ engine.errorMsg() }}</p>
        </section>

        <section class="vd-sp-contacts" *ngIf="idleView() === 'contacts'">
          <p class="vd-sp-hist-empty" *ngIf="!directory()">Annuaire indisponible.</p>
          <ng-container *ngIf="directory() as dir">
            <div class="vd-sp-dir-sec" *ngIf="fieldAgents().length">
              <div class="vd-sp-dir-h">Agents terrain</div>
              <button class="vd-sp-hist-row" *ngFor="let e of fieldAgents()" (click)="dialEntry(e)">
                <span class="material-icons-outlined vd-sp-hist-ic">smartphone</span>
                <span class="vd-sp-hist-num">{{ e.label }}</span>
                <span class="vd-sp-hist-meta">{{ e.dial }}</span>
              </button>
            </div>
            <div class="vd-sp-dir-sec" *ngIf="deskAgents().length">
              <div class="vd-sp-dir-h">Postes softphone</div>
              <button class="vd-sp-hist-row" *ngFor="let e of deskAgents()" (click)="dialEntry(e)">
                <span class="material-icons-outlined vd-sp-hist-ic">headset_mic</span>
                <span class="vd-sp-hist-num">{{ e.label }}</span>
                <span class="vd-sp-hist-meta">{{ e.dial }}</span>
              </button>
            </div>
            <div class="vd-sp-dir-sec" *ngIf="dir.queues.length">
              <div class="vd-sp-dir-h">Files / groupes d'appel</div>
              <button class="vd-sp-hist-row" *ngFor="let e of dir.queues" (click)="dialEntry(e)">
                <span class="material-icons-outlined vd-sp-hist-ic">groups</span>
                <span class="vd-sp-hist-num">{{ e.label }}</span>
                <span class="vd-sp-hist-meta">{{ e.dial }}</span>
              </button>
            </div>
            <div class="vd-sp-dir-sec" *ngIf="dir.ivrs.length">
              <div class="vd-sp-dir-h">Reception / menus</div>
              <button class="vd-sp-hist-row" *ngFor="let e of dir.ivrs" (click)="dialEntry(e)">
                <span class="material-icons-outlined vd-sp-hist-ic">account_tree</span>
                <span class="vd-sp-hist-num">{{ e.label }}</span>
                <span class="vd-sp-hist-meta">{{ e.dial }}</span>
              </button>
            </div>
            <div class="vd-sp-dir-sec" *ngIf="dir.services.length">
              <div class="vd-sp-dir-h">Services du standard</div>
              <button class="vd-sp-hist-row" *ngFor="let e of dir.services" (click)="dialEntry(e)">
                <span class="material-icons-outlined vd-sp-hist-ic">settings_phone</span>
                <span class="vd-sp-hist-num">{{ e.label }}</span>
                <span class="vd-sp-hist-meta">{{ e.dial }}</span>
              </button>
            </div>
          </ng-container>
        </section>

        <section class="vd-sp-history" *ngIf="idleView() === 'history'">
          <p class="vd-sp-hist-notice" *ngIf="histNotice()">{{ histNotice() }}</p>
          <div class="vd-sp-sms" *ngIf="smsFor() as s">
            <div class="vd-sp-sms-to">SMS · {{ s.peer_label || s.peer_dial }}</div>
            <textarea
              class="vd-sp-sms-body"
              rows="3"
              maxlength="480"
              [value]="smsDraft()"
              (input)="smsDraft.set($any($event.target).value)"
              placeholder="Votre message…"></textarea>
            <div class="vd-sp-sms-acts">
              <button type="button" class="vd-sp-sms-cancel" (click)="cancelSms()">Annuler</button>
              <button type="button" class="vd-sp-sms-send" (click)="sendHistSms()" [disabled]="smsBusy() || !smsDraft().trim()">
                {{ smsBusy() ? 'Envoi…' : 'Envoyer' }}
              </button>
            </div>
          </div>
          <p class="vd-sp-hist-empty" *ngIf="!history().length">Aucun appel recent.</p>
          <div class="vd-sp-hist-card" *ngFor="let h of history()" [class.miss]="!h.answered && h.kind !== 'denied'" [class.deny]="h.kind === 'denied'">
            <div class="vd-sp-hist-main">
              <span class="material-icons-outlined vd-sp-hist-ic"
                    [class.in]="h.direction === 'inbound'"
                    [class.miss]="!h.answered || h.kind === 'denied'">
                {{ histIcon(h) }}
              </span>
              <div class="vd-sp-hist-txt">
                <span class="vd-sp-hist-num">{{ h.peer_label || h.dst || h.src || '—' }}</span>
                <span class="vd-sp-hist-meta">
                  {{ h.duration_label || (h.duration_sec ? (h.duration_sec + 's') : '') }}
                  <span *ngIf="h.disposition_label"> · {{ h.disposition_label }}</span>
                </span>
              </div>
            </div>
            <div class="vd-sp-hist-acts">
              <button *ngIf="h.can_redial" type="button" class="vd-sp-hist-play" (click)="redial(h)" aria-label="Rappeler" [disabled]="engine.status()==='offline'">
                <span class="material-icons-outlined">call</span>
              </button>
              <button *ngIf="h.can_sms" type="button" class="vd-sp-hist-play" (click)="openSms(h)" aria-label="SMS">
                <span class="material-icons-outlined">sms</span>
              </button>
              <button *ngIf="h.peer_dial" type="button" class="vd-sp-hist-play" (click)="copyPeer(h)" aria-label="Copier">
                <span class="material-icons-outlined">content_copy</span>
              </button>
              <button
                *ngIf="h.has_recording"
                type="button"
                class="vd-sp-hist-play"
                [class.on]="playingId() === h.id"
                (click)="togglePlay(h, $event)"
                aria-label="Ecouter l'enregistrement">
                <span class="material-icons-outlined">{{ playingId() === h.id ? 'stop' : 'play_arrow' }}</span>
              </button>
            </div>
          </div>
        </section>
      </ng-template>
    </div>
    </ng-container>
  `,
  styleUrls: ['./softphone-widget.component.css'],
})
export class VdSoftphoneWidgetComponent implements OnInit, OnDestroy {
  readonly engine = inject(SoftphoneEngineService);
  private readonly api = inject(TelephonieService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cookies = inject(CookieService);
  private readonly realtime = inject(RealtimeService);

  // Masque sur les routes publiques/auth (pas de softphone avant login)
  // + chats plein écran (bras droit / marketer) où le FAB chevauche le composer.
  private static readonly HIDDEN_ROUTE_RE =
    /^\/(login|signup|CompleteSignUp|complete-signup|auth\/|changer-mot-de-passe|mot-de-passe-oublie|loading|PageNotFound|pos\/terminal|agents\/(shift|terminal)|social-checkout|public\/scan|tg\/app|staff\/login|Welcom|ai-agents\/(marketer|right-hand|accountant)|$)/i;

  // Visibilite OPTIMISTE : la bulle s'affiche des qu'on est authentifie sur une
  // route privee. Elle ne DISPARAIT que si on sait explicitement que le module
  // telephonie est INACTIF (config -> 409). Une erreur reseau/Cloudflare/cache
  // NE cache PAS la bulle (probeState reste 'unknown'). L'utilisateur voit donc
  // toujours le telephone quand il est connecte ; le clic declenche la connexion.
  private readonly routeAuthOk = signal(false);
  private readonly probeState = signal<'unknown' | 'ok' | 'off'>('unknown');
  readonly visible = computed(
    () => this.routeAuthOk() && this.probeState() !== 'off',
  );
  readonly open = signal(false);
  readonly dialNumber = signal('');
  readonly showKeypad = signal(false);
  // Transfert en cours d'appel (REFER).
  readonly showTransfer = signal(false);
  readonly transferTarget = signal('');
  // Vue au repos : clavier, annuaire ou historique.
  readonly idleView = signal<'dial' | 'contacts' | 'history'>('dial');
  readonly history = signal<CallHistoryItem[]>([]);
  readonly directory = signal<SoftphoneDirectory | null>(null);
  readonly playingId = signal<number | null>(null);
  readonly smsFor = signal<CallHistoryItem | null>(null);
  readonly smsDraft = signal('');
  readonly smsBusy = signal(false);
  readonly histNotice = signal('');
  private histNoticeTimer?: ReturnType<typeof setTimeout>;
  private playUrl: string | null = null;
  private playEl?: HTMLAudioElement;
  // Appel entrant "pre-annonce" par le SSE (avant meme l'INVITE SIP) : permet
  // d'afficher l'ecran d'appel + sonner INSTANTANEMENT (latence ~0).
  readonly incomingHint = signal<RealtimeVoipCallEvent | null>(null);
  // La sonnerie/overlay s'affiche si l'INVITE SIP est la OU si le SSE l'annonce.
  readonly ringing = computed(
    () => this.engine.status() === 'incoming' || this.incomingHint() !== null,
  );
  private tick = signal(0);
  private sub?: Subscription;
  private navSub?: Subscription;
  private voipSub?: Subscription;
  private lastProbeAt = 0;
  private autoStarted = false;
  // Sonnerie WebAudio (aucun asset : oscillateur module) + vibration.
  private audioCtx?: AudioContext;
  private ringTimer?: ReturnType<typeof setInterval>;
  private vibrateTimer?: ReturnType<typeof setInterval>;
  private wasRinging = false;
  /** Verrouille raccrocher ~700 ms apres compose/decroche (anti ghost-click). */
  readonly hangupLocked = signal(false);
  private hangupLockTimer?: ReturnType<typeof setTimeout>;
  private lastCallPhase: string | null = null;

  readonly keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

  constructor() {
    // Sonnerie/vibration pilotees par l'etat "ringing" (SSE OU INVITE), reactif.
    effect(() => {
      const r = this.ringing();
      if (r && !this.wasRinging) { this.wasRinging = true; this.startRing(); this.open.set(true); }
      else if (!r && this.wasRinging) { this.wasRinging = false; this.stopRing(); }
    });
    // Quand l'appel est reellement decroche/termine (INVITE), efface le hint SSE.
    effect(() => {
      const s = this.engine.status();
      if (s === 'in_call' || s === 'registered' || s === 'offline') this.incomingHint.set(null);
    });
    // Bloque le bouton rouge juste apres transition vers outgoing/in_call.
    effect(() => {
      const s = this.engine.status();
      if ((s === 'outgoing' || s === 'in_call') && s !== this.lastCallPhase) {
        this.lastCallPhase = s;
        this.armHangupLock();
      } else if (s !== 'outgoing' && s !== 'in_call') {
        this.lastCallPhase = s;
        this.clearHangupLock();
      }
    });
    // Click-to-call depuis listes / drawers : ouvre le panneau tout de suite.
    effect(() => {
      if (this.engine.panelOpenTick() > 0) {
        this.open.set(true);
        if (!this.engine.isBusy()) this.idleView.set('dial');
      }
    });
  }

  private armHangupLock(): void {
    this.hangupLocked.set(true);
    if (this.hangupLockTimer) clearTimeout(this.hangupLockTimer);
    this.hangupLockTimer = setTimeout(() => this.hangupLocked.set(false), 700);
  }

  private clearHangupLock(): void {
    if (this.hangupLockTimer) clearTimeout(this.hangupLockTimer);
    this.hangupLockTimer = undefined;
    this.hangupLocked.set(false);
  }

  safeHangup(): void {
    if (this.hangupLocked()) return;
    this.engine.hangup();
  }

  readonly fabIcon = computed(() => {
    switch (this.engine.status()) {
      case 'incoming': return 'ring_volume';
      case 'in_call':
      case 'outgoing': return 'call';
      default: return 'call';
    }
  });

  readonly elapsed = computed(() => {
    this.tick();
    const start = this.engine.callStartedAt();
    if (!start) return '00:00';
    const sec = Math.floor((Date.now() - start) / 1000);
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  });

  ngOnInit(): void {
    this.sub = interval(1000).subscribe(() => this.tick.update((v) => v + 1));
    this.navSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => { this.evaluateVisibility(); this.handleDeepLink(); });
    this.evaluateVisibility();
    this.handleDeepLink();

    // SSE : un appel entrant reveille le softphone INSTANTANEMENT (latence ~0).
    // Si pas encore enregistre, on s'enregistre pour que l'INVITE WSS arrive.
    this.voipSub = this.realtime.voipCalls$.subscribe((ev) => {
      if (ev?.event === 'missed') { this.incomingHint.set(null); this.stopRing(); return; }
      this.probeState.set('ok');
      // Pre-annonce l'appel (sonne + overlay immediatement) et s'enregistre pour
      // que l'INVITE WSS arrive. Auto-efface au bout de 35s si aucun INVITE.
      this.incomingHint.set(ev);
      setTimeout(() => {
        if (this.engine.status() !== 'incoming' && this.engine.status() !== 'in_call') {
          this.incomingHint.set(null);
        }
      }, 35000);
      this.engine.ensureStarted().catch(() => {});
    });
    // Reveil au retour au premier plan (PWA en arriere-plan -> foreground).
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisible);
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.navSub?.unsubscribe();
    this.voipSub?.unsubscribe();
    this.stopRing();
    this.clearHangupLock();
    this.stopPlayback();
    if (this.histNoticeTimer) clearTimeout(this.histNoticeTimer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisible);
    }
  }

  private onVisible = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      if (this.probeState() === 'ok') this.engine.ensureStarted().catch(() => {});
    }
  };

  /** Deep-link push : /telephonie?vd_answer=<id> (Repondre) ou ?vd_reject=<id>. */
  private handleDeepLink(): void {
    try {
      const qp = this.route.snapshot.queryParamMap;
      const params = new URLSearchParams(window.location.search);
      const answer = qp.get('vd_answer') || params.get('vd_answer');
      const reject = qp.get('vd_reject') || params.get('vd_reject');
      if (answer) {
        this.open.set(true);
        this.engine.armAutoAnswer().catch(() => {});
        this.clearDeepLink();
      } else if (reject) {
        this.engine.hangup().catch(() => {});
        this.clearDeepLink();
      }
    } catch { /* no-op */ }
  }

  private clearDeepLink(): void {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('vd_answer');
      url.searchParams.delete('vd_reject');
      window.history.replaceState({}, '', url.toString());
    } catch { /* no-op */ }
  }

  /**
   * Le softphone apparait si : route privee + authentifie, ET (le module
   * telephonie fournit une config OU un poste est deja connecte). La sonde
   * config est re-tentee sur la page /telephonie (pour refleter une activation
   * ou une creation de poste immediate) avec un anti-rebond de 5 s.
   */
  private evaluateVisibility(): void {
    const url = (this.router.url || '').split('#')[0];
    const authed = !!this.cookies.get('access_token');
    const ok = authed && !VdSoftphoneWidgetComponent.HIDDEN_ROUTE_RE.test(url);
    this.routeAuthOk.set(ok);
    if (!ok) return;
    // Assure le flux SSE (idempotent) pour recevoir les evenements voip_call.
    this.realtime.connect();
    const onTelephonie = url.startsWith('/telephonie');
    const now = Date.now();
    if (now - this.lastProbeAt > 5000) {
      this.lastProbeAt = now;
      this.api.softphoneConfig().subscribe({
        next: () => { this.probeState.set('ok'); this.autoRegister(); },
        error: (e) => {
          // 409 = module telephonie INACTIF -> on cache (seul cas ou on masque).
          // Tout autre code (401/403/404/0/CORS/reseau) -> on NE cache PAS :
          // la bulle reste visible (fail-open), le clic tentera la connexion.
          if (e?.status === 409) this.probeState.set('off');
          else if (this.probeState() === 'unknown') this.probeState.set('unknown');
        },
      });
    } else if (this.probeState() === 'ok') {
      this.autoRegister();
    }
  }

  /**
   * Auto-enregistrement du softphone des que l'utilisateur est habilite (config
   * OK). NE demande PAS le micro (SIP.js n'ouvre le micro qu'a l'acceptation
   * d'un appel) -> aucun prompt intempestif, mais le poste est joignable des
   * l'ouverture de la PWA (indispensable pour recevoir les appels).
   */
  private autoRegister(): void {
    if (this.autoStarted) return;
    this.autoStarted = true;
    this.engine.ensureStarted().catch(() => {});
  }

  async toggle(): Promise<void> {
    const next = !this.open();
    this.open.set(next);
    if (next && this.engine.status() === 'offline') {
      await this.engine.start();
    }
  }

  press(k: string): void {
    this.dialNumber.update((v) => (v + k).slice(0, 24));
  }

  backspace(): void {
    this.dialNumber.update((v) => v.slice(0, -1));
  }

  async startCall(): Promise<void> {
    const n = this.dialNumber().trim();
    if (!n) return;
    await this.engine.call(n);
  }

  /** Transfert aveugle en cours d'appel (REFER via Asterisk + guard). */
  async doTransfer(ev: Event): Promise<void> {
    ev.preventDefault();
    const t = this.transferTarget().trim();
    if (!t) return;
    const ok = await this.engine.transfer(t);
    if (ok) {
      this.showTransfer.set(false);
      this.transferTarget.set('');
    }
  }

  toggleTransfer(): void {
    const next = !this.showTransfer();
    this.showTransfer.set(next);
    if (next) this.ensureDirectory();
  }

  pickTransfer(e: DirectoryEntry): void {
    this.transferTarget.set(e.dial);
  }

  /** Envoie l'appel en cours vers LA messagerie de CE poste (*99 + mon numero). */
  async sendToVoicemail(): Promise<void> {
    const n = this.engine.myNumber();
    if (!n) return;
    if (!confirm('Envoyer cet appel vers votre messagerie vocale ?')) return;
    await this.engine.transfer('*99' + n);
  }

  transferChoices(): DirectoryEntry[] {
    const d = this.directory();
    if (!d) return [];
    return [...d.agents, ...d.queues, ...d.ivrs].slice(0, 16);
  }

  fieldAgents(): DirectoryEntry[] {
    return (this.directory()?.agents || []).filter((a) => a.field);
  }

  deskAgents(): DirectoryEntry[] {
    return (this.directory()?.agents || []).filter((a) => !a.field);
  }

  openContacts(): void {
    this.idleView.set('contacts');
    this.ensureDirectory();
  }

  private ensureDirectory(): void {
    if (this.directory()) return;
    this.api.directory().subscribe({
      next: (d) => this.directory.set(d),
      error: () => this.directory.set({ agents: [], queues: [], ivrs: [], services: [] }),
    });
  }

  async dialEntry(e: DirectoryEntry): Promise<void> {
    if (!e.dial) return;
    this.dialNumber.set(e.dial);
    this.idleView.set('dial');
    await this.engine.call(e.dial);
  }

  togglePlay(h: CallHistoryItem, ev: Event): void {
    ev.stopPropagation();
    if (this.playingId() === h.id) {
      this.stopPlayback();
      return;
    }
    this.api.recordingAudio(h.id, true).subscribe({
      next: (blob) => {
        this.stopPlayback();
        this.playUrl = URL.createObjectURL(blob);
        this.playEl = new Audio(this.playUrl);
        this.playingId.set(h.id);
        this.playEl.onended = () => this.stopPlayback();
        void this.playEl.play().catch(() => this.stopPlayback());
      },
      error: () => this.stopPlayback(),
    });
  }

  private stopPlayback(): void {
    try { this.playEl?.pause(); } catch { /* no-op */ }
    this.playEl = undefined;
    if (this.playUrl) {
      URL.revokeObjectURL(this.playUrl);
      this.playUrl = null;
    }
    this.playingId.set(null);
  }

  /** Ouvre l'onglet historique (recharge la liste a chaque ouverture). */
  openHistory(): void {
    this.idleView.set('history');
    this.cancelSms();
    this.api.history(40).subscribe({
      next: (rows) => this.history.set(rows || []),
      error: () => this.history.set([]),
    });
  }

  histIcon(h: CallHistoryItem): string {
    if (h.kind === 'denied') return 'block';
    if (h.kind === 'service') return 'settings_phone';
    if (h.direction === 'inbound') return h.answered ? 'call_received' : 'phone_missed';
    if (h.direction === 'outbound') return h.answered ? 'call_made' : 'phone_missed_outgoing';
    return 'sync_alt';
  }

  private flashHist(msg: string): void {
    this.histNotice.set(msg);
    if (this.histNoticeTimer) clearTimeout(this.histNoticeTimer);
    this.histNoticeTimer = setTimeout(() => this.histNotice.set(''), 2800);
  }

  openSms(h: CallHistoryItem): void {
    this.smsFor.set(h);
    this.smsDraft.set('');
  }

  cancelSms(): void {
    this.smsFor.set(null);
    this.smsDraft.set('');
    this.smsBusy.set(false);
  }

  sendHistSms(): void {
    const t = this.smsFor();
    const to = t?.peer_dial || '';
    const body = this.smsDraft().trim();
    if (!t || !to || !body) return;
    this.smsBusy.set(true);
    this.api.sendSms(to, body).subscribe({
      next: (r) => {
        this.smsBusy.set(false);
        if (r?.ok) {
          this.flashHist('SMS envoye');
          this.cancelSms();
        } else {
          this.flashHist(r?.error || 'SMS refuse');
        }
      },
      error: (e) => {
        this.smsBusy.set(false);
        const d = e?.error?.detail;
        const msg = (d && typeof d === 'object' && d.message) ? String(d.message)
          : (typeof d === 'string' ? d : 'Impossible d\'envoyer le SMS');
        this.flashHist(msg);
      },
    });
  }

  copyPeer(h: CallHistoryItem): void {
    const num = h.peer_dial || h.peer || '';
    if (!num) return;
    navigator.clipboard?.writeText(num).then(() => this.flashHist('Numero copie'));
  }

  /** Rappelle un correspondant depuis l'historique. */
  async redial(h: CallHistoryItem): Promise<void> {
    const num = h.peer_dial || '';
    if (!num) {
      this.flashHist('Numero non rappelable');
      return;
    }
    this.dialNumber.set(num);
    this.idleView.set('dial');
    await this.engine.dialFromUi(num, h.peer_label || num);
  }

  async accept(): Promise<void> {
    this.stopRing();
    // Si l'INVITE SIP n'est pas encore arrive (SSE en avance), arme l'auto-reponse.
    if (this.engine.status() === 'incoming') await this.engine.answer();
    else await this.engine.armAutoAnswer();
  }

  async reject(): Promise<void> {
    this.stopRing();
    this.incomingHint.set(null);
    await this.engine.hangup();
  }

  // ── Sonnerie (WebAudio, sans asset) + vibration ─────────────────────────
  private startRing(): void {
    // Vibration (Android PWA ; iOS ignore silencieusement).
    try {
      if (navigator.vibrate) {
        const pattern = [400, 200, 400, 200, 400];
        navigator.vibrate(pattern);
        this.vibrateTimer = setInterval(() => navigator.vibrate(pattern), 1600);
      }
    } catch { /* no-op */ }
    // Tonalite de sonnerie (double bip 440/480 Hz facon telephone).
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      this.audioCtx = this.audioCtx || new Ctx();
      const ctx = this.audioCtx!;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const beep = () => {
        const now = ctx.currentTime;
        [440, 480].forEach((freq) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.frequency.value = freq;
          osc.type = 'sine';
          gain.gain.setValueAtTime(0.0001, now);
          gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);
          osc.connect(gain).connect(ctx.destination);
          osc.start(now);
          osc.stop(now + 1.0);
        });
      };
      beep();
      this.ringTimer = setInterval(beep, 3000); // cadence US ring (1s on / 2s off)
    } catch { /* no-op */ }
  }

  private stopRing(): void {
    if (this.ringTimer) { clearInterval(this.ringTimer); this.ringTimer = undefined; }
    if (this.vibrateTimer) { clearInterval(this.vibrateTimer); this.vibrateTimer = undefined; }
    try { if (navigator.vibrate) navigator.vibrate(0); } catch { /* no-op */ }
  }

  statusLabel(): string {
    switch (this.engine.status()) {
      case 'offline': return 'Hors ligne';
      case 'connecting': return 'Connexion…';
      case 'registered': return 'En ligne';
      case 'incoming': return 'Appel entrant';
      case 'outgoing': return 'Appel sortant';
      case 'in_call': return 'En communication';
      case 'error': return 'Erreur';
    }
  }
}
